import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // Template tabs state
  openTemplateTabs: [],       // Array of tab objects: { id, template_name, fabric_id, fabric_name, template_type, vm_config, lang_id }
  activeTabId: null,           // Currently active tab ID
  templateDataCache: {},       // Maps templateId -> fetched template data (Submission Reports)
  canvasDataCache: {},         // Maps templateId -> fetched canvas data (BI Canvas)
  layoutsCache: {},            // Maps templateId -> fetched slide layouts (BI Canvas)

  // Per-tab UI state
  tabActiveViews: {},          // Maps tabId -> 'template' | 'valuemessage' | 'report'
  tabVmConfig: {},             // Maps tabId -> boolean (vm_config flag)

  tabExpandedSections: {},     // Maps tabId -> { sectionKey: bool } for template viewer
  tabRefAgentExpanded: {},     // Maps tabId -> { sectionKey: bool } for ReferenceAgentSelector

  // Form state
  isFormVisible: false,
  editingTemplate: null,       // Template object being edited, or null
  activeCreateType: null,      // 'BI_CANVAS' | 'SUBMISSION_REPORT' | null

  fabricTabsCache: {},
};

const genbiSlice = createSlice({
  name: 'genbi',
  initialState,
  reducers: {

    // ── Template Tabs ──────────────────────────────────────
    addTemplateTab(state, action) {
      state.openTemplateTabs.push(action.payload);
    },

    removeTemplateTab(state, action) {
      const templateId = action.payload;
      state.openTemplateTabs = state.openTemplateTabs.filter(
        (tab) => tab.id !== templateId
      );
      delete state.templateDataCache[templateId];
      delete state.canvasDataCache[templateId];
      delete state.layoutsCache[templateId];
      delete state.tabActiveViews[templateId];
      delete state.tabVmConfig[templateId];
      delete state.tabExpandedSections[templateId];
      delete state.tabRefAgentExpanded[templateId];

      // If closing the active tab, switch to last remaining tab
      if (state.activeTabId === templateId) {
        const remaining = state.openTemplateTabs;
        state.activeTabId =
          remaining.length > 0 ? remaining[remaining.length - 1].id : null;
      }
    },

    updateTemplateTabName(state, action) {
      const { tabId, template_name } = action.payload;
      const tab = state.openTemplateTabs.find(t => t.id === tabId);
      if (tab) {
        tab.template_name = template_name;
      }
    },

    setActiveTabId(state, action) {
      state.activeTabId = action.payload;
    },

    // ── Template Data Cache (Submission Reports) ───────────
    updateTemplateDataCache(state, action) {
      const { templateId, data } = action.payload;
      state.templateDataCache[templateId] = data;
    },

    setTemplateDataCacheFull(state, action) {
      state.templateDataCache = action.payload;
    },

    // ── Canvas Data Cache (BI Canvas) ──────────────────────
    updateCanvasDataCache(state, action) {
      const { templateId, data } = action.payload;
      state.canvasDataCache[templateId] = data;
    },

    setCanvasDataCacheFull(state, action) {
      state.canvasDataCache = action.payload;
    },

    // ── Layouts Cache (BI Canvas) ──────────────────────────
    setLayoutsCacheFull(state, action) {
      state.layoutsCache = action.payload;
    },

    // ── Per-Tab Views ──────────────────────────────────────
    setTabActiveView(state, action) {
      const { tabId, view } = action.payload;
      state.tabActiveViews[tabId] = view;
    },

    setTabVmConfigValue(state, action) {
      const { tabId, config } = action.payload;
      state.tabVmConfig[tabId] = config;
    },

    setTabExpandedSections(state, action) {
      const { tabId, expandedSections } = action.payload;
      state.tabExpandedSections[tabId] = expandedSections;
    },

    setTabRefAgentExpanded(state, action) {
      const { tabId, expandedSections } = action.payload;
      state.tabRefAgentExpanded[tabId] = expandedSections;
    },
    // ── Form State ─────────────────────────────────────────
    setIsFormVisible(state, action) {
      state.isFormVisible = action.payload;
    },

    setEditingTemplate(state, action) {
      state.editingTemplate = action.payload;
    },

    setActiveCreateType(state, action) {
      state.activeCreateType = action.payload;
    },

    // ── Bulk / Reset ───────────────────────────────────────
    resetGenbiState() {
      return initialState;
    },
  },
});

export const {
  addTemplateTab,
  removeTemplateTab,
  updateTemplateTabName,
  setActiveTabId,
  updateTemplateDataCache,
  setTemplateDataCacheFull,
  updateCanvasDataCache,
  setCanvasDataCacheFull,
  setLayoutsCacheFull,
  setTabActiveView,
  setTabVmConfigValue,
  setTabExpandedSections,
  setTabRefAgentExpanded,
  setIsFormVisible,
  setEditingTemplate,
  setActiveCreateType,
  resetGenbiState,
} = genbiSlice.actions;

export default genbiSlice.reducer;